import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GraduationCap, Users, BookOpen, BarChart3, CheckCircle2,
  ArrowRight, Sparkles, Shield, ChevronRight,
} from 'lucide-react';
import { useAuthStore } from '../../../../store/authStore';

// ─── Slides do wizard ───────────────────────────────────────────────────────

const slides = [
  {
    id: 'boas-vindas',
    icon: <Sparkles size={36} style={{ color: '#6c5ce7' }} />,
    tag: 'Bem-vindo(a)!',
    tagColor: '#6c5ce7',
    tagBg: '#f5f3ff',
    titulo: 'Você está no Aluno Conectado',
    descricao:
      'Uma plataforma completa para transformar a gestão da sua escola. ' +
      'Aqui você organiza turmas, acompanha alunos, conecta professores e mantém as famílias sempre informadas.',
    destaque: null,
  },
  {
    id: 'o-que-voce-faz',
    icon: <Shield size={36} style={{ color: '#0ea5e9' }} />,
    tag: 'Seu papel',
    tagColor: '#0ea5e9',
    tagBg: '#e0f2fe',
    titulo: 'Como Diretor(a), você comanda tudo',
    descricao: 'Você tem acesso total à escola. Veja o que está disponível para você:',
    destaque: [
      { icon: <Users size={16} />, texto: 'Montar sua equipe (Coordenação e Secretaria)' },
      { icon: <BookOpen size={16} />, texto: 'Configurar turnos, matérias e turmas' },
      { icon: <GraduationCap size={16} />, texto: 'Cadastrar professores e alunos' },
      { icon: <BarChart3 size={16} />, texto: 'Acompanhar relatórios e indicadores em tempo real' },
    ],
  },
  {
    id: 'trilha',
    icon: <CheckCircle2 size={36} style={{ color: '#10b981' }} />,
    tag: 'Sua trilha',
    tagColor: '#10b981',
    tagBg: '#d1fae5',
    titulo: 'Siga a trilha de configuração',
    descricao:
      'Preparamos 7 etapas simples para você estruturar tudo. ' +
      'Siga na ordem e em poucos minutos sua escola estará 100% operacional.',
    destaque: [
      { icon: <ChevronRight size={16} />, texto: 'Equipe Administrativa' },
      { icon: <ChevronRight size={16} />, texto: 'Turnos Operacionais' },
      { icon: <ChevronRight size={16} />, texto: 'Matérias / Disciplinas' },
      { icon: <ChevronRight size={16} />, texto: 'Turmas → Professores → Alunos → Grade' },
    ],
  },
];

// ─── Componente ──────────────────────────────────────────────────────────────

interface DiretorIntroWizardProps {
  nomeUsuario?: string;
}

export function DiretorIntroWizard({ nomeUsuario }: DiretorIntroWizardProps) {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const slide = slides[step];
  const isLast = step === slides.length - 1;

  const concluirIntroducao = () => {
    try {
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        const updatedUser = { ...currentUser, ultimoLogin: new Date().toISOString() };
        localStorage.setItem('ac_user', JSON.stringify(updatedUser));
        sessionStorage.setItem('ac_user', JSON.stringify(updatedUser));
        useAuthStore.setState({ user: updatedUser });
      }
    } catch (err) {
      console.warn('[DiretorIntroWizard] Erro ao atualizar ultimoLogin:', err);
    }
    navigate('/admin/dashboard');
  };

  function avancar() {
    if (isLast) {
      concluirIntroducao();
    } else {
      setStep(s => s + 1);
    }
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(160deg, #0f172a 0%, #1e293b 50%, #312e81 100%)',
      padding: 16,
    }}>
      <div style={{
        background: '#ffffff', borderRadius: 28, padding: 'clamp(28px,5vw,48px) clamp(24px,5vw,44px)',
        maxWidth: 480, width: '100%',
        boxShadow: '0 30px 80px rgba(0,0,0,.35), 0 0 0 1px rgba(255,255,255,.06)',
        animation: 'fadeIn .35s ease',
      }}>

        {/* Dots de progresso */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 32 }}>
          {slides.map((_, i) => (
            <div key={i} style={{
              width: i === step ? 24 : 8, height: 8, borderRadius: 4,
              background: i === step ? '#6c5ce7' : i < step ? '#a29bfe' : '#e2e8f0',
              transition: 'all .3s',
            }} />
          ))}
        </div>

        {/* Tag do slide */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: slide.tagBg, color: slide.tagColor,
          padding: '4px 12px', borderRadius: 20, fontSize: '.72rem', fontWeight: 700,
          letterSpacing: '.04em', marginBottom: 20,
        }}>
          {slide.tag}
        </div>

        {/* Ícone */}
        <div style={{
          width: 72, height: 72, borderRadius: 20,
          background: slide.tagBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 20, border: `2px solid ${slide.tagColor}22`,
        }}>
          {slide.icon}
        </div>

        {/* Título */}
        {nomeUsuario && step === 0 && (
          <p style={{ fontSize: '.82rem', color: '#94a3b8', marginBottom: 4, fontWeight: 600 }}>
            Olá, {nomeUsuario}!
          </p>
        )}
        <h2 style={{ fontSize: 'clamp(1.1rem,3vw,1.35rem)', fontWeight: 800, color: '#1e293b', margin: '0 0 12px', lineHeight: 1.25 }}>
          {slide.titulo}
        </h2>
        <p style={{ color: '#64748b', fontSize: '.88rem', lineHeight: 1.65, margin: '0 0 24px' }}>
          {slide.descricao}
        </p>

        {/* Lista de destaque */}
        {slide.destaque && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
            {slide.destaque.map((item, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: '#f8fafc', borderRadius: 10, padding: '10px 14px',
                border: '1px solid #e2e8f0',
              }}>
                <span style={{ color: slide.tagColor, flexShrink: 0 }}>{item.icon}</span>
                <span style={{ fontSize: '.84rem', color: '#334155', fontWeight: 500 }}>{item.texto}</span>
              </div>
            ))}
          </div>
        )}

        {/* Botão */}
        <button
          onClick={avancar}
          style={{
            width: '100%', padding: '14px 24px',
            background: 'linear-gradient(135deg, #6c5ce7, #a29bfe)',
            color: 'white', border: 'none', borderRadius: 14,
            fontWeight: 700, fontSize: '.95rem', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            boxShadow: '0 6px 24px rgba(108,92,231,.35)', transition: 'opacity .2s',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '.9'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
        >
          {isLast ? (
            <>Começar a configurar <ArrowRight size={18} /></>
          ) : (
            <>Próximo <ChevronRight size={18} /></>
          )}
        </button>

        {/* Pular */}
        {!isLast && (
          <button
            onClick={concluirIntroducao}
            style={{
              width: '100%', marginTop: 10, padding: '10px 0',
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#94a3b8', fontSize: '.8rem', fontWeight: 500,
            }}
          >
            Pular introdução →
          </button>
        )}
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
      `}</style>
    </div>
  );
}
